import { BackendError, Service } from '@genial/services';
// TODO: Properly export meta response type
import { MetaResponse } from '@genial/services/dist/models';
import { Utils } from '@genial/utils';
import { DefaultValue, selector, selectorFamily } from 'recoil';

import { TableHeadItemOrderBy } from '../../components/TableHead';
import { FiltersToApplyProps } from '../../hooks/useFilterTransformer';
import { blankMetaResponse } from '../../utils/blankMetaResponse';
import { advisorIdToApisSelector } from '../master/selector';

import { orderHistoryDateFilterState, OrderHistoryDateFilterStateProps } from './atom';
import { screenName } from './constants';
import { OrderHistoryType } from './types';

export type OrderSequenceStatusQueryItem = Utils.ArrayElement<
  NonNullable<Awaited<ReturnType<typeof Service.Orders.V1.getSequenceStatus>>['data']>
>;

export type OrderSequenceStatusQueryData = OrderSequenceStatusQueryItem[] | null | undefined;

export const orderHistoryDateFilterSelectedSelector = selector<
  OrderHistoryDateFilterStateProps['optionSelected']
>({
  key: 'orderHistory/DateFilterSelectedSelector',
  get: ({ get }) => {
    return get(orderHistoryDateFilterState).optionSelected;
  },
  set: ({ set }, newValue) => {
    if (newValue instanceof DefaultValue) {
      return;
    }
    set(orderHistoryDateFilterState, (prevState) => ({
      ...prevState,
      optionSelected: newValue,
    }));
  },
});

export const orderSequenceStatusQuery = selector<{
  data: OrderSequenceStatusQueryData | undefined;
}>({
  key: 'OrderHistory/statusSequence',
  get: async () => {
    try {
      const { data } = await Service.Orders.V1.getSequenceStatus({ screenName });

      return { data };
    } catch (error) {
      const backendError = error as BackendError;

      if (backendError.statusCode === 404) {
        return { data: [], meta: blankMetaResponse() };
      }

      throw error;
    }
  },
});

export const ordersHistoryQuery = selectorFamily<
  | {
      meta: MetaResponse;
      data: OrderHistoryType[];
    }
  | undefined,
  {
    page: number;
    limit: number;
    searchTerm: string;
    filtersToApply: FiltersToApplyProps;
    orderBy?: TableHeadItemOrderBy;
  }
>({
  key: 'orderHistory/ordersHistoryQuery',
  get:
    ({ page, limit, searchTerm, filtersToApply, orderBy }) =>
    async ({ get }) => {
      try {
        const advisorSelected = get(advisorIdToApisSelector);
        const dateFilter = get(orderHistoryDateFilterSelectedSelector);

        const { data, meta } = await Service.Orders.V1.getFilter({
          screenName,
          page,
          limit,
          searchTerm: searchTerm ? searchTerm : undefined,
          advisorId: advisorSelected,
          dateFilter: dateFilter.value,
          ...filtersToApply,
          sortItems: orderBy
            ? [{ sort: orderBy.value, sortDirection: orderBy.direction }]
            : undefined,
        });

        const normalizedData =
          data?.map((item) => ({
            ...item.order,
            orderEvents: item.orderEvents,
            requestUserFullName: item.requestUserFullName,
          })) || [];

        return { data: normalizedData, meta };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], meta: blankMetaResponse() };
        }

        throw error;
      }
    },
});

export type OrdersConsolidatedQueryItem = Utils.ArrayElement<
  NonNullable<Awaited<ReturnType<typeof Service.Orders.V1.getConsolidatedPeriod>>['data']>
>;

export type OrdersConsolidatedQueryData = OrdersConsolidatedQueryItem[] | null | undefined;

export const ordersConsolidatedQuery = selectorFamily<
  | {
      data: OrdersConsolidatedQueryData | undefined;
    }
  | undefined,
  {
    searchTerm: string;
    filtersToApply: FiltersToApplyProps;
  }
>({
  key: 'OrderHistory/ordersConsolidated',
  get:
    ({ searchTerm, filtersToApply }) =>
    async ({ get }) => {
      try {
        const advisorSelected = get(advisorIdToApisSelector);
        const dateFilter = get(orderHistoryDateFilterSelectedSelector);

        const { data } = await Service.Orders.V1.getConsolidatedFilter({
          dateFilter: dateFilter.value,
          screenName,
          advisorId: advisorSelected,
          searchTerm: searchTerm ? searchTerm : undefined,
          ...filtersToApply,
        });

        return { data };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], meta: blankMetaResponse() };
        }

        throw error;
      }
    },
});
